import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Review {
  id: string;
  rating: number;
  review_text: string;
  service_date: string;
  created_at: string;
  customers: {
    full_name: string;
  };
}

export default function ReviewsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    rating: 5,
    reviewText: '',
    serviceDate: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  useEffect(() => {
    checkAuth();
    fetchReviews();
  }, []);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setIsLoggedIn(true);
      const { data } = await supabase
        .from('customers')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (data) setCustomerId(data.id);
    }
  };

  const fetchReviews = async () => {
    const { data, error } = await supabase
      .from('reviews')
      .select(`
        *,
        customers (
          full_name
        )
      `)
      .eq('is_approved', true)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setReviews(data);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) return;

    setStatus('submitting');

    try {
      const { error } = await supabase.from('reviews').insert([{
        customer_id: customerId,
        rating: formData.rating,
        review_text: formData.reviewText,
        service_date: formData.serviceDate,
        is_approved: true
      }]);

      if (error) throw error;

      setStatus('success');
      setFormData({ rating: 5, reviewText: '', serviceDate: '' });
      setShowForm(false);
      fetchReviews();
      setTimeout(() => setStatus('idle'), 3000);
    } catch (error) {
      setStatus('error');
      console.error('Error submitting review:', error);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <i
        key={i}
        className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400 text-xl`}
      ></i>
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-yellow-50">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-5xl md:text-6xl font-black text-blue-600 mb-4" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Customer Reviews
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              See what our customers are saying about our trash can cleaning service!
            </p>

            {isLoggedIn ? (
              <button
                onClick={() => setShowForm(!showForm)}
                className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
              >
                {showForm ? 'Cancel' : 'Write a Review'}
              </button>
            ) : (
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/login')}
                className="bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
              >
                Log In to Write a Review
              </button>
            )}
          </motion.div>

          {showForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="bg-white rounded-3xl p-8 shadow-2xl mb-12"
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Share Your Experience</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">Your Rating</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, rating: star })}
                        className="cursor-pointer"
                      >
                        <i className={`ri-star-${star <= formData.rating ? 'fill' : 'line'} text-3xl ${star <= formData.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Service Date</label>
                  <input
                    type="date"
                    value={formData.serviceDate}
                    onChange={(e) => setFormData({ ...formData, serviceDate: e.target.value })}
                    required
                    className="w-full h-12 px-4 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Your Review</label>
                  <textarea
                    value={formData.reviewText}
                    onChange={(e) => setFormData({ ...formData, reviewText: e.target.value })}
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us about your experience..."
                    className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-red-500 focus:outline-none resize-none text-sm"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2 text-right">{formData.reviewText.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 rounded-full text-lg font-bold shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}
                >
                  {status === 'submitting' ? 'Submitting...' : 'Submit Review'}
                </button>

                {status === 'success' && (
                  <div className="bg-green-100 text-green-800 px-4 py-3 rounded-2xl text-center font-bold">
                    Thank you for your review!
                  </div>
                )}
              </form>
            </motion.div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <div className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Loading reviews...
              </div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-2xl font-bold text-gray-600" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                No reviews yet. Be the first to write one!
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((review) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-shadow"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-1">{renderStars(review.rating)}</div>
                    <span className="text-sm text-gray-500">
                      {new Date(review.service_date).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-4 leading-relaxed">{review.review_text}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold">
                      {review.customers.full_name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-bold text-gray-800">{review.customers.full_name}</p>
                      <p className="text-xs text-gray-500">Verified Customer</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}